import { SimpleDocCommand } from '../../apps/DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';

function countWords(text: string): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
}

function getHeadingLevel(attrs: any): number | null {
  if (!attrs) return null;
  return attrs.level || attrs.heading_level || null;
}

export const entity_get_metadata: SimpleDocCommand = {
  id: 'entity_get_metadata',
  logicalName: 'entity_get_metadata',
  version: 'v2',

  description: `Get entity statistics and metadata without reading full content.

**SUPPORTED ENTITIES:**
Documents (doc_*, pdf_*, file_*), Notes (note_*), Sheets (sheet_*), Canvas (canvas_*), Presentations (presentation_*)

**USE CASES:**
- Check entity size (node count, word count)
- Get document structure (headings, node types)
- Fast stats lookup without content

**DOCUMENTS ONLY:**
Documents support advanced selectors ("all", indexRange, range, nodeIds, query) and options (includeAttributes, includePreview).

**RETURNS:**
- Documents: totalNodes, totalWords, nodeTypes, headingLevels, structuralElements
- Other entities: nodeCount, wordCount, app-specific stats (totalCells, totalSlides, etc.)

**EXAMPLES:**

Get document stats:
{ "entityId": "doc_meadow_quicksilver_jellyfish" }

Get note metadata:
{ "entityId": "note_cascade_telephone_helix" }

Get document range:
{ "entityId": "doc_meadow_quicksilver_jellyfish", "selector": "{\\"indexRange\\": {\\"from\\": 0, \\"to\\": 10}}" }
`,

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Entity ID (doc_*, pdf_*, file_*, note_*, sheet_*, canvas_*, slide_*, or presentation_*)',
        required: true,
        validator: (value: string) => {
          if (!value) return 'Entity ID is required';
          const validPrefixes = ['doc_', 'pdf_', 'file_', 'note_', 'sheet_', 'canvas_', 'slide_', 'presentation_'];
          const isValid = validPrefixes.some(prefix => value.startsWith(prefix));
          if (!isValid) {
            return `Invalid entity ID. Must start with: ${validPrefixes.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'selector',
        type: 'string',
        description: 'Documents only: What metadata to retrieve (optional, defaults to "all"). Can be "all" or JSON string with indexRange, range, nodeIds, or query.',
        required: false,
        defaultValue: 'all'
      },
      {
        name: 'includeAttributes',
        type: 'boolean',
        description: 'Documents only: Include full ProseMirror attributes (default: false).',
        required: false,
        defaultValue: false
      },
      {
        name: 'includePreview',
        type: 'boolean',
        description: 'Documents only: Include text preview (first 100 chars) for each node (default: false).',
        required: false,
        defaultValue: false
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId } = params;

    if (!entityId || typeof entityId !== 'string') {
      return {
        status: 'error',
        message: 'Entity ID is required',
        error: { code: 'missing_entity_id', category: 'validation' as const, details: 'entityId parameter is required and must be a string' },
        format: 'plaintext'
      };
    }

    const isDocument = entityId.startsWith('doc_') ||
                       entityId.startsWith('pdf_') ||
                       entityId.startsWith('file_');

    console.log('[entity_get_metadata] Routing based on entityId:', { entityId, isDocument });

    if (isDocument) {
      return handleDocumentMetadata(params, context);
    } else {
      return handleEntityMetadata(params, context);
    }
  }
};

async function handleDocumentMetadata(params: any, context: CommandExecutionContext): Promise<StandardToolResult> {
  const startTime = Date.now();

  try {
    const { entityId: id, selector = 'all', includeAttributes = false, includePreview = false } = params;

    console.log('[entity_get_metadata:doc] ========== TOOL EXECUTION START ==========');
    console.log('[entity_get_metadata:doc] Input parameters:', { id, selector: typeof selector === 'string' ? selector.substring(0, 200) : selector, includeAttributes, includePreview, timestamp: new Date().toISOString() });

    if (!id) {
      console.error('[entity_get_metadata:doc] Validation failed: Missing document ID');
      return { status: 'error', message: 'Missing document ID', error: { code: 'missing_id', category: 'validation' as const, details: 'id parameter is required' }, format: 'plaintext' };
    }

    const { entityResolver } = await import('../../services/entityResolver');
    const resolveStartTime = Date.now();
    const resolved = await entityResolver.resolveEntity(id, context);
    const resolveTime = Date.now() - resolveStartTime;

    console.log('[entity_get_metadata:doc] Entity resolution:', { success: !!resolved?.entity, timeTaken: `${resolveTime}ms`, entityType: resolved?.entity?.type, entityName: resolved?.entity?.name });

    if (!resolved || !resolved.entity) {
      console.error('[entity_get_metadata:doc] Entity resolution failed:', { id });
      return { status: 'error', message: 'Document not found', error: { code: 'not_found', category: 'validation' as const, details: `Could not find document with ID "${id}"` }, format: 'plaintext' };
    }

    const documentName = resolved.entity.name || 'document';

    const { createDocumentReader } = await import('../../apps/DocsApp/services/doc/DocumentReader');
    const readerStartTime = Date.now();
    const { reader, error: readerError } = await createDocumentReader(id, context);
    const readerTime = Date.now() - readerStartTime;

    if (readerError || !reader) {
      console.error('[entity_get_metadata:doc] Failed to create document reader:', { id, error: readerError || 'Reader creation failed', timeTaken: `${readerTime}ms` });
      return { status: 'error', message: 'Document not accessible', error: { code: 'not_accessible', category: 'system' as const, details: readerError || `Could not access document "${documentName}"` }, format: 'plaintext' };
    }

    console.log('[entity_get_metadata:doc] Document reader created:', { id, format: reader.format, documentName: reader.documentName, timeTaken: `${readerTime}ms` });

    const parseStartTime = Date.now();
    let parsedSelector: any = selector;
    let selectorMode = 'all';

    if (typeof selector === 'object' && selector !== null && selector !== 'all') {
      parsedSelector = selector;
    } else if (typeof selector === 'string' && selector !== 'all' && (selector.startsWith('{') || selector.startsWith('['))) {
      try {
        parsedSelector = JSON.parse(selector);
      } catch (e: any) {
        const truncatedSelector = selector.substring(0, 100) + (selector.length > 100 ? '...' : '');
        console.error('[entity_get_metadata:doc] Selector JSON parse failed:', { error: e.message, selector: truncatedSelector });
        return { status: 'error', message: 'Invalid selector JSON', error: { code: 'invalid_selector', category: 'validation' as const, details: `Selector appears to be JSON but failed to parse: ${e.message}. Selector: ${truncatedSelector}` }, format: 'plaintext' };
      }
    }

    if (typeof parsedSelector === 'object') {
      if (parsedSelector.indexRange) selectorMode = 'indexRange';
      else if (parsedSelector.range) selectorMode = 'range';
      else if (parsedSelector.nodeIds) selectorMode = 'nodeIds';
      else if (parsedSelector.query) selectorMode = 'query';
    }

    const parseTime = Date.now() - parseStartTime;
    console.log('[entity_get_metadata:doc] Selector parsed:', { selectorMode, parsedSelector: typeof parsedSelector === 'string' ? parsedSelector : JSON.stringify(parsedSelector).substring(0, 200), parseTime: `${parseTime}ms` });

    if (selectorMode === 'all') {
      console.log('[entity_get_metadata:doc] Processing "all" selector - reading full document');
      const readAllStartTime = Date.now();
      const allContent = reader.readAll();
      const readAllTime = Date.now() - readAllStartTime;
      const allNodes = allContent.nodes;

      console.log('[entity_get_metadata:doc] Document read complete:', { nodeCount: allNodes.length, readTime: `${readAllTime}ms` });

      if (allNodes.length === 0) {
        console.log('[entity_get_metadata:doc] Empty document detected');
        return {
          status: 'success',
          message: `Document "${documentName}" is empty (0 nodes)`,
          data: { type: 'document_metadata_full', documentId: id, documentName, selector: 'all', statistics: { totalNodes: 0, totalWords: 0, nodeTypes: {}, headingLevels: {}, structuralElements: { images: 0, tables: 0, codeBlocks: 0, blockquotes: 0, horizontalRules: 0 }, distribution: { averageWordsPerNode: 0, longestNode: null, shortestNode: null } } },
          format: 'json'
        };
      }

      const processStartTime = Date.now();
      const nodeTypes: Record<string, number> = {};
      const headingLevels: Record<string, number> = {};
      let totalWords = 0;
      let longestNode: any = null;
      let shortestNode: any = null;

      for (const node of allNodes) {
        nodeTypes[node.type] = (nodeTypes[node.type] || 0) + 1;
        if (node.type === 'heading') {
          const level = getHeadingLevel(node.attrs);
          if (level !== null) headingLevels[level.toString()] = (headingLevels[level.toString()] || 0) + 1;
        }
        const nodeWords = countWords(node.text || '');
        totalWords += nodeWords;
        if (!longestNode || nodeWords > longestNode.wordCount) longestNode = { id: node.id, type: node.type, wordCount: nodeWords };
        if (!shortestNode || nodeWords < shortestNode.wordCount) shortestNode = { id: node.id, type: node.type, wordCount: nodeWords };
      }

      const avgWordsPerNode = allNodes.length > 0 ? Math.round(totalWords / allNodes.length) : 0;
      const structuralElements = { images: nodeTypes['image'] || 0, tables: nodeTypes['table'] || 0, codeBlocks: nodeTypes['code_block'] || 0, blockquotes: nodeTypes['blockquote'] || 0, horizontalRules: nodeTypes['horizontal_rule'] || 0 };
      const result = { type: 'document_metadata_full', documentId: id, documentName, selector: 'all', statistics: { totalNodes: allNodes.length, totalWords, nodeTypes, headingLevels, structuralElements, distribution: { averageWordsPerNode: avgWordsPerNode, longestNode, shortestNode } } };
      const totalTime = Date.now() - startTime;
      console.log('[entity_get_metadata:doc] ========== "ALL" MODE SUCCESS ==========');
      console.log('[entity_get_metadata:doc] Performance:', { totalTime: `${totalTime}ms`, nodeCount: allNodes.length, totalWords });
      return { status: 'success', message: `Document "${documentName}": ${allNodes.length} nodes, ${totalWords.toLocaleString()} words`, data: result, format: 'json' };
    }

    console.log('[entity_get_metadata:doc] Processing range selector:', { selectorMode });
    let nodes: any[] = [];
    let rangeInfo: any = {};
    let globalStartIndex: number | undefined = undefined;

    if (selectorMode === 'indexRange') {
      const indexRangeParam = parsedSelector.indexRange;
      let fromIndex: number;
      let toIndex: number;
      if ('from' in indexRangeParam && 'to' in indexRangeParam) { fromIndex = indexRangeParam.from; toIndex = indexRangeParam.to; }
      else if ('start' in indexRangeParam && 'count' in indexRangeParam) { fromIndex = indexRangeParam.start; toIndex = indexRangeParam.start + indexRangeParam.count - 1; }
      else return { status: 'error', message: 'Invalid indexRange', error: { code: 'invalid_index_range', category: 'validation' as const, details: 'indexRange requires either {"from": number, "to": number} or {"start": number, "count": number}' }, format: 'plaintext' };
      if (fromIndex < 0 || toIndex < fromIndex) return { status: 'error', message: 'Invalid indexRange', error: { code: 'invalid_range', category: 'validation' as const, details: `Invalid range: from=${fromIndex}, to=${toIndex}. Ensure from >= 0 and to >= from.` }, format: 'plaintext' };
      const allContent = reader.readAll();
      const allNodes = allContent.nodes;
      if (toIndex >= allNodes.length) return { status: 'error', message: 'Index out of bounds', error: { code: 'out_of_bounds', category: 'validation' as const, details: `Index ${toIndex} exceeds document length (${allNodes.length} nodes). Use indices 0-${allNodes.length - 1}.` }, format: 'plaintext' };
      nodes = allNodes.slice(fromIndex, toIndex + 1);
      rangeInfo = { from: fromIndex, to: toIndex, totalDocumentNodes: allNodes.length };
      globalStartIndex = fromIndex;
    } else if (selectorMode === 'range') {
      const rangeParam = parsedSelector.range;
      if ('start' in rangeParam && 'count' in rangeParam) return { status: 'error', message: 'Invalid range selector', error: { code: 'invalid_range_format', category: 'validation' as const, details: 'range selector with start/count format is not supported. Use {"from": "node_id", "to": "node_id"} instead.' }, format: 'plaintext' };
      if (!('from' in rangeParam) || !('to' in rangeParam)) return { status: 'error', message: 'Invalid range selector', error: { code: 'missing_range_params', category: 'validation' as const, details: 'range selector requires {"from": "node_id", "to": "node_id"}' }, format: 'plaintext' };
      try { const rangeResult = reader.readNodeRange(rangeParam.from, rangeParam.to); nodes = rangeResult.nodes; rangeInfo = { from: rangeParam.from, to: rangeParam.to }; }
      catch (error: any) { return { status: 'error', message: 'Range read failed', error: { code: 'range_read_failed', category: 'execution' as const, details: error.message || 'Unknown error' }, format: 'plaintext' }; }
    } else if (selectorMode === 'nodeIds') {
      try { const allContent = reader.readAll(); const requestedIds = parsedSelector.nodeIds; nodes = allContent.nodes.filter(n => requestedIds.includes(n.id)); rangeInfo = { nodeIds: requestedIds, found: nodes.length }; }
      catch (error: any) { return { status: 'error', message: 'NodeIds read failed', error: { code: 'nodeids_failed', category: 'execution' as const, details: error.message || 'Unknown error' }, format: 'plaintext' }; }
    } else if (selectorMode === 'query') {
      try {
        const queryText = typeof parsedSelector.query === 'string' ? parsedSelector.query : parsedSelector.query.text;
        const searchResult = reader.search(queryText);
        nodes = searchResult.nodes;
        rangeInfo = { query: queryText, matchCount: nodes.length };
      } catch (error: any) { return { status: 'error', message: 'Query search failed', error: { code: 'query_failed', category: 'execution' as const, details: error.message || 'Unknown error' }, format: 'plaintext' }; }
    }

    const nodeMetadata = nodes.map((node, index) => {
      const text = node.text || '';
      const wordCount = countWords(text);
      const charCount = text.length;
      const isEmpty = text.trim().length === 0;
      const metadata: any = { id: node.id, type: node.type, wordCount, charCount, isEmpty, index };
      if (globalStartIndex !== undefined) metadata.globalIndex = globalStartIndex + index;
      if (includePreview && !isEmpty) { const previewLength = 100; metadata.preview = text.length > previewLength ? text.substring(0, previewLength) + '...' : text; }
      if (includeAttributes && node.attrs) { metadata.attrs = node.attrs; }
      else if (node.type === 'heading') { const level = getHeadingLevel(node.attrs); if (level !== null) metadata.headingLevel = level; }
      if (node.children && node.children.length > 0) metadata.hasChildren = true;
      return metadata;
    });

    const nodeTypes: Record<string, number> = {};
    let totalWords = 0;
    for (const nodeMeta of nodeMetadata) { nodeTypes[nodeMeta.type] = (nodeTypes[nodeMeta.type] || 0) + 1; totalWords += nodeMeta.wordCount; }
    const avgWordsPerNode = nodeMetadata.length > 0 ? Math.round(totalWords / nodeMetadata.length) : 0;
    const totalTime = Date.now() - startTime;
    console.log('[entity_get_metadata:doc] ========== RANGE MODE SUCCESS ==========');
    console.log('[entity_get_metadata:doc] Performance:', { totalTime: `${totalTime}ms`, nodeCount: nodeMetadata.length, totalWords });

    return {
      status: 'success',
      message: `Metadata for ${nodeMetadata.length} nodes: ${totalWords.toLocaleString()} words`,
      data: { type: 'node_metadata_range', documentId: id, documentName, selector: parsedSelector, selectorMode, rangeInfo, nodes: nodeMetadata, summary: { nodeCount: nodeMetadata.length, totalWords, nodeTypes, averageWordsPerNode: avgWordsPerNode } },
      format: 'json'
    };

  } catch (error: any) {
    const totalTime = Date.now() - startTime;
    console.error('[entity_get_metadata:doc] ========== EXECUTION FAILED ==========');
    console.error('[entity_get_metadata:doc] Error:', error);
    return { status: 'error', message: 'Metadata retrieval failed', error: { code: 'execution_error', category: 'execution' as const, details: error.message || 'Unknown error' }, format: 'plaintext' };
  }
}

async function handleEntityMetadata(params: any, context: CommandExecutionContext): Promise<StandardToolResult> {
  const { entityId } = params;
  console.log('[entity_get_metadata:entity] Getting metadata for:', entityId);
  try {
    const app = await getApplicationForEntity(entityId);
    if (!app) return { status: 'error', message: 'Entity not found or not a v2 entity', error: { code: 'not_found', category: 'validation' as const, details: `Could not find entity ${entityId}` }, format: 'plaintext' };

    const indexStats = (app as any).indexManager?.getStats();
    let appStats: any = {};
    if (typeof (app as any).getDocumentStats === 'function') appStats = await (app as any).getDocumentStats();
    if (typeof (app as any).getCanvasStats === 'function') appStats = await (app as any).getCanvasStats();
    if (typeof (app as any).getSheetStats === 'function') appStats = await (app as any).getSheetStats();
    if (typeof (app as any).getPresentationStats === 'function') appStats = await (app as any).getPresentationStats();
    if (typeof (app as any).getNoteStats === 'function') appStats = await (app as any).getNoteStats();

    const entityType = (app as any).constructor?.name || 'unknown';
    console.log('[entity_get_metadata:entity] ✅ Metadata retrieved:', { entityId, entityType, hasIndexStats: !!indexStats, hasAppStats: Object.keys(appStats).length > 0 });

    return { status: 'success', message: `Retrieved metadata for ${entityId}`, data: { entityId, entityType, indexStats, ...appStats }, format: 'json' };
  } catch (error: any) {
    console.error('[entity_get_metadata:entity] Error:', error);
    return { status: 'error', message: 'Failed to get metadata', error: { code: 'metadata_error', category: 'execution' as const, details: error.message }, format: 'plaintext' };
  }
}
